// Demo data: a small hand-written family that exercises every feature, and a large
// generated tree for trying out navigation and performance.

import { newEvent, newId, newPerson } from './people';
import { withLocations } from './places';
import { type CiteTarget, citationsAt, setCitations } from './research';
import { addRelationship, findRelationship, newTree, upsertGroup, upsertPerson, upsertSource } from './tree';
import type { Citation, EventType, GenderIdentity, Group, Id, ParentType, PartnerRel, Person, PlaceLocation, Relationship, SocialRel, Source, Tree } from './types';

// Where the demo places are (looked up once on OpenStreetMap), so the Map shows them without
// looking anything up.
const LOCATIONS: Record<string, PlaceLocation> = {
  'Bronx, New York': { lat: 40.8467, lon: -73.8786 },
  'Brooklyn, New York': { lat: 40.6526, lon: -73.9497 },
  'Enugu, Nigeria': { lat: 6.5536, lon: 7.4143 },
  'Houston, Texas': { lat: 29.7589, lon: -95.3677 },
  'Onitsha, Nigeria': { lat: 6.1462, lon: 6.8019 },
  'Ponce, Puerto Rico': { lat: 18.0668, lon: -66.614 },
  'Portland, Oregon': { lat: 45.5202, lon: -122.6742 },
  'San Juan, Puerto Rico': { lat: 18.3842, lon: -66.0534 },
  'Seattle, Washington': { lat: 47.6038, lon: -122.3301 },
  'Boston, Massachusetts': { lat: 42.3588, lon: -71.0578 },
  'Chicago, Illinois': { lat: 41.8756, lon: -87.6244 },
  'Austin, Texas': { lat: 30.2649, lon: -97.7474 },
  'Fresno, California': { lat: 36.7394, lon: -119.7848 },
  'Duluth, Minnesota': { lat: 46.7838, lon: -92.1053 },
  'Savannah, Georgia': { lat: 32.079, lon: -81.0921 },
  'Tucson, Arizona': { lat: 32.2229, lon: -110.9748 },
  'Albany, New York': { lat: 42.6512, lon: -73.755 },
  'Toledo, Ohio': { lat: 41.6529, lon: -83.5378 },
  'Spokane, Washington': { lat: 47.6572, lon: -117.4235 },
};

/** The tree with every demo place it uses on the map. */
function located(t: Tree): Tree {
  const used = new Set<string>();
  for (const p of Object.values(t.people)) for (const e of p.events) if (e.place) used.add(e.place);
  for (const r of Object.values(t.relationships)) if (r.place) used.add(r.place);
  return withLocations(t, [...used].flatMap((place) => (LOCATIONS[place] ? [[place, LOCATIONS[place]] as [string, PlaceLocation]] : [])));
}

interface Spec {
  given: string;
  surname: string;
  g: GenderIdentity;
  label?: string;
  pronouns?: string;
  born?: string;
  bornAt?: string;
  died?: string;
  diedAt?: string;
  job?: string;
  nickname?: string;
  tags?: string[];
  notes?: string;
  hiddenBirthName?: string;
  events?: [EventType, string, string?][];
}

/** The sample family with their photos, which load separately so the app's first load stays small. */
export async function loadSampleTree(): Promise<Tree> {
  return sampleTree((await import('../assets/sample-photos.json')).default);
}

/** The sample family. `photos` are profile photos by person key (scripts/build-sample-photos.mjs). */
export function sampleTree(photos: Record<string, string> = {}): Tree {
  let t = newTree('Sample: The Rivera–Okafor Family');
  const ids: Record<string, string> = {};

  const add = (key: string, s: Spec) => {
    const names: Person['names'] = [{ given: s.given, surname: s.surname, type: s.hiddenBirthName ? 'chosen' : 'name', nickname: s.nickname }];
    if (s.hiddenBirthName) names.push({ given: s.hiddenBirthName, surname: s.surname, type: 'deadname' });
    const p = newPerson({
      names,
      gender: { identity: s.g, label: s.label },
      pronouns: s.pronouns,
      tags: s.tags ?? [],
      notes: s.notes ?? '',
      // Everyone in this family without a recorded death is alive.
      living: s.died || s.diedAt ? null : true,
      photo: photos[key],
    });
    if (s.born || s.bornAt) p.events.push(newEvent('birth', { date: s.born, place: s.bornAt }));
    if (s.died || s.diedAt) p.events.push(newEvent('death', { date: s.died, place: s.diedAt }));
    if (s.job) p.events.push(newEvent('occupation', { label: s.job }));
    for (const [type, date, place] of s.events ?? []) p.events.push(newEvent(type, { date, place }));
    ids[key] = p.id;
    t = upsertPerson(t, p);
  };
  const parent = (p: string, c: string, type: ParentType = 'biological') => {
    t = addRelationship(t, { kind: 'parent', a: ids[p], b: ids[c], type }).tree;
  };
  const partner = (a: string, b: string, type: PartnerRel['type'] = 'married', status: PartnerRel['status'] = 'current', start?: string, end?: string) => {
    t = addRelationship(t, { kind: 'partner', a: ids[a], b: ids[b], type, status, start, end }).tree;
  };
  const social = (a: string, b: string, type: SocialRel['type'], label?: string, start?: string) => {
    t = addRelationship(t, { kind: 'social', a: ids[a], b: ids[b], type, label, start }).tree;
  };

  // Generation 1
  add('rosa', { given: 'Rosa', surname: 'Rivera', g: 'woman', pronouns: 'she/her', born: '14 FEB 1938', bornAt: 'San Juan, Puerto Rico', died: '2 NOV 2015', diedAt: 'Bronx, New York', job: 'Seamstress' });
  add('manuel', { given: 'Manuel', surname: 'Rivera', g: 'man', pronouns: 'he/him', born: 'ABT 1935', bornAt: 'Ponce, Puerto Rico', died: '1999', diedAt: 'Bronx, New York', job: 'Subway conductor' });
  add('grace', { given: 'Grace', surname: 'Okafor', g: 'woman', born: '1941', bornAt: 'Enugu, Nigeria', job: 'Nurse', tags: ['immigrant'], events: [['immigration', '1963', 'Houston, Texas']] });
  add('emeka', { given: 'Emeka', surname: 'Okafor', g: 'man', born: '1939', bornAt: 'Onitsha, Nigeria', died: '2010', diedAt: 'Houston, Texas', job: 'Civil engineer', tags: ['immigrant'], events: [['immigration', '1961', 'Houston, Texas']] });
  // Generation 2
  add('lucia', { given: 'Lucía', surname: 'Rivera', g: 'woman', pronouns: 'she/her', born: '3 JUN 1962', bornAt: 'Bronx, New York', job: 'Public school teacher' });
  add('carmen', { given: 'Carmen', surname: 'Rivera', g: 'woman', born: '1965', bornAt: 'Bronx, New York', job: 'Lawyer' });
  add('tomas', { given: 'Tomás', surname: 'Rivera', g: 'man', born: '1968', bornAt: 'Bronx, New York', job: 'Chef' });
  add('ada', { given: 'Ada', surname: 'Okafor', g: 'woman', pronouns: 'she/her', born: '22 SEP 1964', bornAt: 'Houston, Texas', job: 'Architect' });
  add('chidi', { given: 'Chidi', surname: 'Okafor', g: 'man', born: '1967', bornAt: 'Houston, Texas', job: 'Pharmacist' });
  add('jordan', { given: 'Jordan', surname: 'Blake', g: 'nonbinary', pronouns: 'they/them', born: '1970', bornAt: 'Portland, Oregon', job: 'Sound engineer' });
  add('mike', { given: 'Michael', surname: 'Chen', g: 'man', nickname: 'Mike', born: '1964', job: 'Accountant' });
  add('dee', { given: 'Denise', surname: 'Harper', g: 'woman', nickname: 'Dee', born: '1966', job: 'Bus driver' });
  // Generation 3
  add('sam', { given: 'Sam', surname: 'Rivera-Okafor', g: 'nonbinary', pronouns: 'they/them', born: '12 APR 1995', bornAt: 'Brooklyn, New York', job: 'Software developer', notes: 'Came out as nonbinary in 2016.', hiddenBirthName: 'Samantha', events: [['coming-out', '2016'], ['hrt-start', '14 OCT 2019']] });
  add('nia', { given: 'Nia', surname: 'Rivera-Okafor', g: 'woman', pronouns: 'she/her', born: '1998', bornAt: 'Brooklyn, New York', job: 'Graduate student' });
  add('leo', { given: 'Leo', surname: 'Rivera-Blake', g: 'man', pronouns: 'he/him', born: '2004', bornAt: 'Brooklyn, New York', notes: 'Raised by three parents: Lucía, Ada, and Jordan.' });
  add('isabel', { given: 'Isabel', surname: 'Chen', g: 'woman', born: '1992', job: 'Doctor' });
  add('marcus', { given: 'Marcus', surname: 'Chen', g: 'man', born: '1994', bornAt: 'Seattle, Washington' });
  add('kai', { given: 'Kai', surname: 'Harper', g: 'custom', label: 'Two-Spirit', pronouns: 'they/he', born: '1993' });
  add('obi', { given: 'Obinna', surname: 'Okafor', g: 'man', nickname: 'Obi', born: '1996' });
  // Generation 4
  add('mateo', { given: 'Mateo', surname: 'Chen', g: 'man', born: '2021' });
  add('river', { given: 'River', surname: 'Rivera-Okafor', g: 'nonbinary', pronouns: 'they/them', born: '2023' });
  // Found family & friends
  add('pat', { given: 'Patricia', surname: 'Moore', g: 'woman', nickname: 'Mother Pat', pronouns: 'she/her', born: '1958', job: 'Community organizer', notes: 'Took in queer kids who needed a home for decades.', tags: ['chosen family'] });
  add('ty', { given: 'Tyrell', surname: 'Moore', g: 'man', pronouns: 'he/him', born: '1994', job: 'Dancer' });
  add('ash', { given: 'Ash', surname: 'Nakamura', g: 'nonbinary', pronouns: 'xe/xem', born: '1996', job: 'Illustrator' });
  add('val', { given: 'Valentina', surname: 'Cruz', g: 'woman', pronouns: 'she/her', born: '1995', job: 'Paramedic' });
  add('noor', { given: 'Noor', surname: 'Haddad', g: 'woman', born: '1994', job: 'Librarian' });
  add('ben', { given: 'Ben', surname: 'Goldberg', g: 'man', born: '1960', job: 'Mechanic', notes: 'Lived next door to the Riveras for 30 years.' });

  // Family structure
  partner('rosa', 'manuel', 'married', 'widowed', '1960');
  partner('grace', 'emeka', 'married', 'widowed', '1962');
  for (const c of ['lucia', 'carmen', 'tomas']) { parent('rosa', c); parent('manuel', c); }
  for (const c of ['ada', 'chidi']) { parent('grace', c); parent('emeka', c); }
  partner('lucia', 'ada', 'married', 'current', '2004', undefined);
  partner('lucia', 'mike', 'married', 'divorced', '1990', '1993');
  partner('ada', 'jordan', 'partner', 'current', '2001');
  partner('carmen', 'dee', 'married', 'current', '2015');
  // Sam and Nia: Lucía and Ada's kids (Sam via donor, adopted by Ada)
  parent('lucia', 'sam'); parent('ada', 'sam', 'adoptive');
  parent('lucia', 'nia'); parent('ada', 'nia', 'adoptive');
  // Leo has three parents
  parent('lucia', 'leo'); parent('ada', 'leo'); parent('jordan', 'leo');
  // Lucía's first marriage
  parent('lucia', 'isabel'); parent('mike', 'isabel');
  // Mike remarried and had Marcus
  partner('mike', 'dee', 'married', 'divorced', '1993', '1998');
  parent('mike', 'marcus'); parent('dee', 'marcus');
  parent('dee', 'kai');
  parent('chidi', 'obi');
  partner('isabel', 'kai', 'married', 'current', '2019');
  parent('isabel', 'mateo'); parent('kai', 'mateo');
  partner('sam', 'ash', 'partner', 'current', '2019');
  parent('sam', 'river'); parent('ash', 'river');

  // Found family: Mother Pat's house
  parent('pat', 'ty', 'adoptive');
  parent('pat', 'sam', 'chosen');
  parent('pat', 'val', 'chosen');
  t = addRelationship(t, { kind: 'sibling', a: ids.ty, b: ids.noor, type: 'chosen' }).tree;
  social('sam', 'val', 'best-friend', undefined, '2010');
  social('sam', 'noor', 'close-friend');
  social('nia', 'noor', 'friend');
  social('ben', 'rosa', 'family-friend');
  social('pat', 'lucia', 'family-friend');
  social('pat', 'noor', 'mentor');

  const group = (g: Omit<Group, 'id'>) => {
    t = upsertGroup(t, { ...g, id: newId('g') });
  };
  group({ name: 'House of Moore', kind: 'found-family', description: 'Chosen family that formed around Mother Pat in the 2010s.', color: '#c2410c', members: [ids.pat, ids.ty, ids.sam, ids.ash, ids.val].map((personId, i) => ({ personId, role: i === 0 ? 'House mother' : undefined })) });
  group({ name: 'Tuesday Book Club', kind: 'friend-group', description: 'Has met every Tuesday since college.', color: '#0f766e', members: [ids.sam, ids.noor, ids.val, ids.nia].map((personId) => ({ personId })) });
  group({ name: 'Rivera household (Bronx)', kind: 'household', description: 'The apartment on Grand Concourse.', color: '#4f46e5', members: [ids.rosa, ids.manuel, ids.lucia, ids.carmen, ids.tomas].map((personId) => ({ personId })) });

  // Sources, and the facts they back up.
  const source = (s: Omit<Source, 'id'>) => {
    const id = newId('s');
    t = upsertSource(t, { ...s, id });
    return id;
  };
  const event = (key: string, type: EventType): CiteTarget => ({ personId: ids[key], eventId: t.people[ids[key]].events.find((e) => e.type === type)!.id });
  const rel = (kind: Relationship['kind'], a: string, b: string): CiteTarget => ({ relId: findRelationship(t, kind, ids[a], ids[b])!.id });
  const cite = (sourceId: Id, target: CiteTarget, c: Omit<Citation, 'sourceId'> = {}) => {
    t = setCitations(t, target, [...citationsAt(t, target), { sourceId, ...c }]);
  };

  const rosaMarriage = source({ title: 'Marriage certificate of Manuel and Rosa Rivera', kind: 'Vital record', publication: 'Puerto Rico Demographic Registry', date: '1960', place: 'San Juan, Puerto Rico', repository: 'Rivera family papers', callNumber: 'Box 1, folder 1' });
  cite(rosaMarriage, rel('partner', 'rosa', 'manuel'), { quality: 3 });

  const directory = source({ title: 'Bronx city directory, 1970', kind: 'Directory', date: '1970', place: 'Bronx, New York', repository: 'Online city directories', url: 'https://example.com/directories/bronx-1970', text: 'Rivera Manl (Rosa) condr NYC Transit h Grand Concourse' });
  cite(directory, event('manuel', 'occupation'), { page: 'p. 1123', quality: 3 });

  const luciaBirth = source({ title: 'Birth certificate: Lucía Rivera', kind: 'Vital record', publication: 'New York City Department of Health', date: '3 JUN 1962', place: 'Bronx, New York', repository: 'Rivera family papers', callNumber: 'Box 1, folder 3', notes: 'The family’s certified copy. It names both parents.' });
  cite(luciaBirth, event('lucia', 'birth'), { page: 'Certificate 1962-04817', quality: 3 });
  cite(luciaBirth, rel('parent', 'rosa', 'lucia'), { quality: 3 });
  cite(luciaBirth, rel('parent', 'manuel', 'lucia'), { quality: 3 });

  const familyTree = source({ title: 'Family tree drawn by Tomás Rivera', author: 'Tomás Rivera', kind: 'Family tree', date: '1998', repository: 'Rivera family papers', callNumber: 'Box 2, folder 9', notes: 'Drawn from memory for Isabel’s first-grade project. Some dates are guesses.' });
  cite(familyTree, event('manuel', 'birth'), { quality: 0, note: 'Gives 1934; his other papers say about 1935.' });

  const letters = source({ title: 'Letters from Rosa to Carmen', author: 'Rosa Rivera', kind: 'Letters', date: 'FROM 1985 TO 2012', repository: 'Rivera family papers', callNumber: 'Box 2', notes: 'About forty letters, mostly in Spanish, written while Carmen was at law school and after.' });
  cite(letters, event('manuel', 'death'), { page: 'Letter of 20 May 1999', quality: 2, note: 'Describes his last weeks in the hospital and the neighbors who visited.' });
  cite(letters, rel('social', 'ben', 'rosa'), { page: 'Letter of 3 March 1991', quality: 2, note: 'Ben has fixed the building’s boiler again, and won’t take money for it.' });

  const obituary = source({
    title: 'Rosa Rivera, 77, seamstress who dressed half the Grand Concourse',
    kind: 'Newspaper',
    publication: 'The Concourse Ledger',
    date: '8 NOV 2015',
    place: 'Bronx, New York',
    url: 'https://example.com/concourse-ledger/2015/11/08/rosa-rivera',
    text: 'Rosa Rivera, who sewed wedding dresses and school uniforms for her neighbors for fifty years, died on November 2 at her home in the Bronx. She was 77. Born in San Juan, she came to New York in 1961 with her husband, Manuel, a subway conductor, who died in 1999. She is survived by her children, Lucía, Carmen, and Tomás, and four grandchildren.',
  });
  cite(obituary, event('rosa', 'death'), { page: 'p. 14', quality: 2 });
  cite(obituary, event('rosa', 'occupation'), { page: 'p. 14', quality: 2 });
  cite(obituary, event('manuel', 'death'), { page: 'p. 14', quality: 2 });

  const interview = source({ title: 'Interview with Grace Okafor', author: 'Nia Rivera-Okafor', kind: 'Oral history', date: '14 JUL 2019', place: 'Houston, Texas', repository: 'Family recordings', notes: 'Recorded on a phone at Grace’s kitchen table; about two hours.', text: 'I trained as a nurse in Enugu. Emeka went ahead to Houston for his engineering degree, and I followed him in 1963 with one suitcase and my nursing certificate. Ada was born the next year.' });
  cite(interview, event('grace', 'birth'), { page: '00:02:10', quality: 2 });
  cite(interview, event('grace', 'occupation'), { page: '00:05:40', quality: 2 });
  cite(interview, event('grace', 'immigration'), { page: '00:14:45', quality: 2 });
  cite(interview, rel('partner', 'grace', 'emeka'), { page: '00:11:20', quality: 2 });
  cite(interview, event('emeka', 'birth'), { page: '00:12:05', quality: 1, note: 'Grace wasn’t sure whether he was born in Onitsha or a town nearby.' });

  const naturalization = source({ title: 'Certificate of naturalization: Emeka Okafor', kind: 'Naturalization record', publication: 'U.S. District Court, Southern District of Texas', date: '1971', place: 'Houston, Texas', repository: 'Okafor family papers', callNumber: 'Folder 2' });
  cite(naturalization, event('emeka', 'birth'), { quality: 3, note: 'Gives his birthplace as Onitsha, Nigeria.' });
  cite(naturalization, event('emeka', 'immigration'), { page: 'Date of arrival', quality: 3 });

  const luciaAda = source({ title: 'Marriage certificate: Lucía Rivera and Ada Okafor', kind: 'Vital record', publication: 'Commonwealth of Massachusetts', date: '2004', place: 'Boston, Massachusetts', repository: 'Rivera family papers', callNumber: 'Box 3, folder 2', notes: 'They married in Boston the summer Massachusetts allowed it, seven years before New York did.' });
  cite(luciaAda, rel('partner', 'lucia', 'ada'), { quality: 3 });

  const adoption = source({ title: 'Adoption decree: Sam and Nia', kind: 'Court record', publication: 'Family Court of the State of New York, Kings County', date: '2005', place: 'Brooklyn, New York', repository: 'Rivera family papers', callNumber: 'Box 3, folder 5', notes: 'A second-parent adoption, which made Ada their legal parent too.' });
  cite(adoption, rel('parent', 'ada', 'sam'), { quality: 3 });
  cite(adoption, rel('parent', 'ada', 'nia'), { quality: 3 });

  const house = source({ title: 'House of Moore: our story', author: 'Patricia Moore', kind: 'Website', date: '2018', url: 'https://example.com/house-of-moore', notes: 'The house’s own history, with photos from twenty years of balls.' });
  cite(house, rel('parent', 'pat', 'ty'), { quality: 2, note: 'Pat adopted Ty in 2001, when he was seven.' });
  cite(house, rel('parent', 'pat', 'sam'), { quality: 2 });
  cite(house, rel('parent', 'pat', 'val'), { quality: 2 });

  t = { ...t, homePersonId: ids.sam };
  return located(t);
}

// ---------------------------------------------------------------- large generated tree

const GIVEN_W = ['Mary', 'Ana', 'Grace', 'Ruth', 'Ida', 'Mae', 'Lena', 'Rosa', 'June', 'Iris', 'Opal', 'Nell', 'Esme', 'Lucía', 'Amara', 'Yuki', 'Priya', 'Zoe', 'Hana', 'Leila'];
const GIVEN_M = ['John', 'Luis', 'Amos', 'Elijah', 'Otto', 'Hugo', 'Felix', 'Rafael', 'Kofi', 'Arjun', 'Tomás', 'Jonah', 'Silas', 'Ezra', 'Omar', 'Kenji', 'Diego', 'Theo', 'Malik', 'Ivan'];
const GIVEN_N = ['Alex', 'Robin', 'Sage', 'Jules', 'Rowan', 'Quinn', 'Avery', 'Ash', 'Kit', 'River'];
const SURNAMES = ['Smith', 'Garcia', 'Nguyen', 'Okafor', 'Kowalski', 'Haddad', 'Tanaka', 'Silva', 'Johansson', 'Patel', 'Cohen', 'Murphy', 'Rossi', 'Kim', 'Novak', 'Mensah', 'Dubois', 'Fischer', 'Reyes', 'Walker'];
const PLACES = ['Boston, Massachusetts', 'Chicago, Illinois', 'Austin, Texas', 'Fresno, California', 'Duluth, Minnesota', 'Savannah, Georgia', 'Tucson, Arizona', 'Albany, New York', 'Toledo, Ohio', 'Spokane, Washington'];

/** Deterministic pseudo-random tree with roughly `target` people across many generations. */
export function generateLargeTree(target = 2000, seed = 42): Tree {
  let s = seed;
  const rand = () => {
    s = (s * 1664525 + 1013904223) % 4294967296;
    return s / 4294967296;
  };
  const pick = <T,>(arr: T[]) => arr[Math.floor(rand() * arr.length)];
  const t = newTree('Generated tree');
  const people: Tree['people'] = {};
  const rels: Tree['relationships'] = {};
  const mk = (surname: string, year: number): Person => {
    const r = rand();
    const g: GenderIdentity = r < 0.47 ? 'woman' : r < 0.94 ? 'man' : 'nonbinary';
    const given = g === 'woman' ? pick(GIVEN_W) : g === 'man' ? pick(GIVEN_M) : pick(GIVEN_N);
    const p = newPerson({
      names: [{ given, surname, type: 'name' }],
      gender: { identity: g },
      pronouns: g === 'nonbinary' ? 'they/them' : undefined,
    });
    p.events.push(newEvent('birth', { date: String(year), place: pick(PLACES) }));
    if (year < 1945) p.events.push(newEvent('death', { date: String(year + 50 + Math.floor(rand() * 40)) }));
    people[p.id] = p;
    return p;
  };
  const link = (a: string, b: string, kind: 'parent' | 'partner') => {
    const id = newId('r');
    rels[id] =
      kind === 'parent'
        ? { id, kind, a, b, type: 'biological' }
        : { id, kind, a, b, type: rand() < 0.8 ? 'married' : 'partner', status: rand() < 0.85 ? 'current' : 'divorced' };
  };
  // Start with founding couples and expand generation by generation.
  let gen: { p: Person; year: number }[] = [];
  for (let i = 0; i < 8; i++) {
    const year = 1780 + Math.floor(rand() * 10);
    const a = mk(pick(SURNAMES), year);
    gen.push({ p: a, year });
  }
  let count = gen.length;
  let homeId = gen[0].p.id;
  while (count < target && gen.length) {
    const next: typeof gen = [];
    for (const { p, year } of gen) {
      if (count >= target) break;
      if (year > 2000) continue; // too young for a partner and kids
      // Partner from outside the tree (or sometimes same-gender partner).
      const py = year + Math.floor(rand() * 6) - 3;
      const partner = mk(pick(SURNAMES), py);
      count++;
      link(p.id, partner.id, 'partner');
      const kids = 1 + Math.floor(rand() * 4);
      for (let k = 0; k < kids && count < target; k++) {
        const cy = year + 22 + Math.floor(rand() * 15);
        if (cy > 2024) continue;
        const c = mk(p.names[0].surname, cy);
        count++;
        link(p.id, c.id, 'parent');
        link(partner.id, c.id, 'parent');
        // Not every child continues the line, which keeps branches varied.
        if (rand() < 0.8) next.push({ p: c, year: cy });
      }
    }
    gen = next;
    if (gen.length) homeId = gen[Math.floor(gen.length / 2)].p.id;
  }
  return located({ ...t, name: `Generated tree (${count.toLocaleString('en-US')} people)`, people, relationships: rels, homePersonId: homeId });
}
